import { motion } from "framer-motion";
import { ExternalLink, Users, FileText, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import { useActiveAds } from "@/hooks/useActiveAds";
import { majorParties, otherParties, nonParliamentaryParties } from "@/data/parties";
import { usePartiesStore, getValidatedPartyDisplayData } from "@/data/partiesStore";
import { Link } from "react-router-dom";
import { getSectionContent } from "@/data/cmsStore";
import { useState, useEffect, useMemo } from "react";
import { getPostersConfig, PostersConfig } from "@/data/postersStore";
import { getElectionResults, NationalElectionResult } from "@/data/electionResultsStore";
import { regionsMapData } from "@/data/mapData";
import { useSapStore, SAPCandidate } from "@/data/sapStore";
import { fetchPublicCandidates } from "@/data/candidatesStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const PartiesSection = () => {
  const { t, lang } = useI18n();
  const profiles = usePartiesStore(state => state.profiles);

  const [cms, setCms] = useState(() => getSectionContent("parties", "home"));
  const [postersConfig, setPostersConfig] = useState<PostersConfig>(getPostersConfig());
  const [electionResults, setElectionResults] = useState<NationalElectionResult>(getElectionResults());
  
  const sapCandidates = useSapStore(state => state.candidates);
  const [selectedSapRegion, setSelectedSapRegion] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<any | null>(null);
  const [publicCandidates, setPublicCandidates] = useState<any[]>([]);

  useEffect(() => {
    const handleCmsChange = () => setCms(getSectionContent("parties", "home"));
    const handlePostersConfig = () => setPostersConfig(getPostersConfig());
    const handleElectionResults = () => setElectionResults(getElectionResults());
    
    fetchPublicCandidates().then(data => setPublicCandidates(data));

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "kaynin_posters_config") {
        setPostersConfig(getPostersConfig());
      }
    };
    
    window.addEventListener("cmsLayoutChanged", handleCmsChange);
    window.addEventListener("cmsLayoutDraftChanged", handleCmsChange);
    window.addEventListener("stagingModeChanged", handleCmsChange);
    window.addEventListener("postersConfigChanged", handlePostersConfig);
    window.addEventListener("electionResultsUpdated", handleElectionResults);
    window.addEventListener("storage", handleStorageChange);
    
    // Initial fetch handled by Zustand automatically or via hook
    
    return () => {
      window.removeEventListener("cmsLayoutChanged", handleCmsChange);
      window.removeEventListener("cmsLayoutDraftChanged", handleCmsChange);
      window.removeEventListener("stagingModeChanged", handleCmsChange);
      window.removeEventListener("postersConfigChanged", handlePostersConfig);
      window.removeEventListener("electionResultsUpdated", handleElectionResults);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const isSilenceActive = useMemo(() => {
    if (postersConfig.forceSilenceElectoral) return true;
    if (!postersConfig.autoSilenceElectoral) return false;
    const silenceDate = new Date("2026-09-23T00:00:00");
    return new Date() >= silenceDate;
  }, [postersConfig.autoSilenceElectoral, postersConfig.forceSilenceElectoral]);

  const effectiveShowSeats = !isSilenceActive && postersConfig.showSeats && !postersConfig.lockPosters;
  const effectiveShowLink = !isSilenceActive && postersConfig.showPartyLink && !postersConfig.lockPosters;
  const isVisibleProgram = !isSilenceActive && postersConfig.showProgramLink;
  const isVisibleSite = !isSilenceActive && postersConfig.showWebsiteLink;
  const isLocked = !!postersConfig.lockPosters;

  if (postersConfig.disableAll) {
    return null;
  }

  const getPartySeats = (acronym: string, originalSeats: number) => {
    if (!effectiveShowSeats || isSilenceActive) return 0;
    if (postersConfig.autoReorderByResults && electionResults.results.length > 0) {
       const res = electionResults.results.find(r => r.partyAcronym === acronym);
       if (res) return res.seats;
    }
    return originalSeats;
  };

  return (
    <section className="py-10 bg-card">
      <div className="container">
        <motion.div
          className="text-center mb-8 max-w-2xl mx-auto flex flex-col items-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-secondary/10 text-secondary font-body text-base md:text-lg font-extrabold mb-1.5 border border-secondary/20 animate-pulse tracking-widest uppercase">
            {t.parties.tag}
          </span>
          <h2 className="text-3xl md:text-5xl font-display font-extrabold text-foreground tracking-tight">
            {cms[`parties_title_${lang}`] || (
              <>
                {t.parties.title1}<span className="text-gradient-warm">{t.parties.titleHighlight}</span>
              </>
            )}
          </h2>
          <p className="mt-1.5 text-muted-foreground font-body mx-auto text-lg leading-relaxed whitespace-pre-line mb-2">
            {cms[`parties_desc_${lang}`] || t.parties.description}
          </p>
        </motion.div>

        {/* All Parliamentary parties */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-6">
          {[...majorParties, ...otherParties]
            .sort((a, b) => {
               if (postersConfig.autoReorderByResults) {
                  return getPartySeats(b.acronym, b.seats) - getPartySeats(a.acronym, a.seats);
               }
               return b.seats - a.seats;
            })
            .map((party, i) => {
              const currentSeats = getPartySeats(party.acronym, party.seats);
              const staticName = t.parties.partiesNames[party.acronym] || party.acronym;
              const staticFocus = t.parties.partiesFocus[party.acronym] || "";
              const pData = getValidatedPartyDisplayData(party.acronym, staticName, staticFocus, lang as any);
              return (
                <motion.div
                  key={party.acronym}
                  className="group relative flex flex-col bg-card rounded-2xl shadow-md border border-border overflow-hidden md:hover:shadow-xl md:hover:-translate-y-2 transition-all duration-500"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <div 
                    className="absolute top-0 left-0 w-full h-1.5 z-10 transition-all duration-500 group-hover:h-2" 
                    style={{ background: party.color }} 
                  />
                  <div className="relative h-24 md:h-40 flex items-center justify-center p-3 md:p-6 bg-gradient-to-b from-muted/50 to-transparent">
                    {effectiveShowLink ? (
                      <Link to={`/parti/${party.acronym}`} className="w-full h-full flex items-center justify-center">
                        {pData.logoUrl ? (
                          <img 
                            src={pData.logoUrl} 
                            alt={`Logo ${party.acronym}`} 
                            className="w-full h-full object-contain filter drop-shadow-sm md:hover:drop-shadow-md transition-all duration-500 md:group-hover:scale-110" 
                          />
                        ) : (
                          <span className="text-4xl md:text-6xl drop-shadow-sm transition-transform duration-500 md:group-hover:scale-110">{party.symbol}</span>
                        )}
                      </Link>
                    ) : (
                      pData.logoUrl ? (
                        <img 
                          src={pData.logoUrl} 
                          alt={`Logo ${party.acronym}`} 
                          className="w-full h-full object-contain filter drop-shadow-sm md:hover:drop-shadow-md transition-all duration-500 md:group-hover:scale-110" 
                        />
                      ) : (
                        <span className="text-4xl md:text-6xl drop-shadow-sm transition-transform duration-500 md:group-hover:scale-110">{party.symbol}</span>
                      )
                    )}
                  </div>
                  <div className="p-3 md:p-6 flex flex-col flex-grow bg-card">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-3 md:mb-4 gap-2 md:gap-0">
                      <span
                        className="inline-flex shrink-0 w-fit items-center justify-center text-[10px] md:text-xs font-body font-bold px-2 py-0.5 md:px-3 md:py-1 rounded-full text-white shadow-sm"
                        style={{ background: party.color, color: "#fff" }}
                      >
                        {t.parties.partiesNames[party.acronym] || party.acronym}
                      </span>
                      <div className="flex w-fit shrink-0 items-center gap-1 md:gap-1.5 text-[10px] md:text-sm font-semibold text-muted-foreground font-body bg-muted/50 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full">
                        <Users className="w-3 h-3 md:w-4 md:h-4" />
                        <span>{currentSeats} <span className="hidden md:inline">{t.parties.seats}</span></span>
                      </div>
                    </div>
                    <h3 className="text-sm md:text-lg font-display font-bold text-foreground leading-tight mb-1.5 md:mb-2 line-clamp-2">
                      {effectiveShowLink ? (
                        <Link to={`/parti/${party.acronym}`} className="hover:text-primary transition-colors">
                          {pData.fullName}
                        </Link>
                      ) : (
                        <span className="text-foreground">{pData.fullName}</span>
                      )}
                    </h3>
                    <p className="text-[10px] md:text-sm text-muted-foreground font-body leading-relaxed line-clamp-2 mb-4 md:mb-6 flex-grow">
                      {pData.focus}
                    </p>
                    <div className="flex flex-col gap-1.5 md:gap-2 mt-auto">
                      {isVisibleProgram && (
                        <Button size="sm" asChild={!isLocked} disabled={isLocked} className="w-full font-body font-semibold group/btn text-white h-8 md:h-10 text-[10px] md:text-sm px-2" style={!isLocked ? { backgroundColor: party.color } : undefined}>
                          {isLocked ? (
                            <span className="flex items-center justify-center w-full">
                              <FileText className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 shrink-0" />
                              <span className="truncate">{t.parties.viewProgram}</span>
                            </span>
                          ) : (
                            <Link to={`/parti/${party.acronym}`}>
                              <FileText className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 transition-transform md:group-hover/btn:scale-110 shrink-0" />
                              <span className="truncate">{t.parties.viewProgram}</span>
                            </Link>
                          )}
                        </Button>
                      )}
                      {isVisibleSite && pData.website && (
                        <Button variant="outline" size="sm" asChild={!isLocked} disabled={isLocked} className="w-full font-body text-[10px] md:text-xs h-7 md:h-9 px-2 hidden sm:flex">
                          {isLocked ? (
                            <span className="flex items-center justify-center w-full">
                              <Globe className="w-3 md:w-3.5 h-3 md:h-3.5 mr-1 shrink-0" />
                              <span className="truncate">{t.parties.visitWebsite}</span>
                              <ExternalLink className="w-2.5 h-2.5 md:w-3 md:h-3 ml-1 opacity-50 shrink-0" />
                            </span>
                          ) : (
                            <a href={pData.website} target="_blank" rel="noopener noreferrer">
                              <Globe className="w-3 md:w-3.5 h-3 md:h-3.5 mr-1 shrink-0" />
                              <span className="truncate">{t.parties.visitWebsite}</span>
                              <ExternalLink className="w-2.5 h-2.5 md:w-3 md:h-3 ml-1 opacity-50 shrink-0" />
                            </a>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
        </div>



        {/* Non-parliamentary parties */}
        <motion.div
          className="mt-16 bg-muted/30 p-8 rounded-3xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-display font-bold text-foreground mb-3">
              {t.parties.nonParliamentaryTitle}
            </h3>
            <p className="text-muted-foreground font-body">
              {t.parties.nonParliamentaryDescription}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {nonParliamentaryParties.map((party, i) => {
              const staticName = t.parties.partiesNames[party.acronym] || party.acronym;
              const pData = getValidatedPartyDisplayData(party.acronym, staticName, "", lang as any);
              return (
                <motion.div
                  key={party.acronym}
                  className="group relative flex flex-col p-4 rounded-xl bg-card border border-border/60 hover:border-border shadow-sm hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: (i % 6) * 0.05 }}
                >
                  {effectiveShowLink ? (
                    <Link to={`/parti/${party.acronym}`} className="flex flex-col items-center w-full h-full justify-between">
                      <div 
                        className="absolute top-0 left-0 w-full h-1 rounded-t-xl transition-all duration-300 opacity-50 group-hover:opacity-100" 
                        style={{ background: party.color }} 
                      />
                      
                      <div className="w-16 h-16 flex items-center justify-center mb-3">
                        {pData.logoUrl ? (
                          <img 
                            src={pData.logoUrl} 
                            alt={`Logo ${party.acronym}`} 
                            className="max-w-full max-h-full object-contain filter drop-shadow-sm transition-transform duration-300 md:group-hover:scale-110" 
                          />
                        ) : (
                          <span className="text-3xl drop-shadow-sm transition-transform duration-300 md:group-hover:scale-110">{party.symbol}</span>
                        )}
                      </div>
                      
                      <span
                        className="inline-block text-[10px] font-body font-bold px-2 py-0.5 mb-2 rounded-full text-white shadow-sm shrink-0"
                        style={{ background: party.color }}
                      >
                        {t.parties.partiesNames[party.acronym] || party.acronym}
                      </span>

                      <p className="text-[10px] font-body font-medium text-foreground text-center line-clamp-2 leading-tight">
                        {pData.fullName}
                      </p>
                    </Link>
                  ) : (
                    <div className="flex flex-col items-center w-full h-full justify-between cursor-default">
                      <div 
                        className="absolute top-0 left-0 w-full h-1 rounded-t-xl transition-all duration-300 opacity-50" 
                        style={{ background: party.color }} 
                      />
                      
                      <div className="w-16 h-16 flex items-center justify-center mb-3">
                        {pData.logoUrl ? (
                          <img 
                            src={pData.logoUrl} 
                            alt={`Logo ${party.acronym}`} 
                            className="max-w-full max-h-full object-contain filter drop-shadow-sm" 
                          />
                        ) : (
                          <span className="text-3xl drop-shadow-sm">{party.symbol}</span>
                        )}
                      </div>
                      
                      <span
                        className="inline-block text-[10px] font-body font-bold px-2 py-0.5 mb-2 rounded-full text-white shadow-sm shrink-0"
                        style={{ background: party.color }}
                      >
                        {t.parties.partiesNames[party.acronym] || party.acronym}
                      </span>

                      <p className="text-[10px] font-body font-medium text-foreground text-center line-clamp-2 leading-tight">
                        {pData.fullName}
                      </p>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Candidats Indépendants (SAP) et Partis */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-8 max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary font-body text-sm font-bold mb-1.5 border border-primary/20">
              {lang === 'ar' ? 'انتخابات 2026' : lang === 'amz' ? 'ⵉⵙⵜⴰⵢⵏ 2026' : 'Élections 2026'}
            </span>
            <h3 className="text-2xl font-display font-bold text-foreground mb-3">
              {lang === 'ar' ? 'المترشحون (أحزاب ومستقلون)' : lang === 'amz' ? 'ⵉⵎⵙⵜⴰⵢⵏ (ⵉⵎⵓⵔⴰⵏ ⴷ ⵉⵎⴰⵏⴰⵔⵏ)' : 'Candidats (Partis & Indépendants)'}
            </h3>
            <p className="text-muted-foreground font-body">
              {lang === 'ar' ? 'اكتشف جميع المواطنين المترشحين في جهتك، سواء باسم حزب سياسي أو كمستقلين.' : lang === 'amz' ? 'ⴰⴼ ⴰⴽⴽⵯ ⵉⵏⴰⵎⵓⵔⵏ ⵍⵍⵉ ⵉⵜⵜⵎⵃⵉⵣⵡⴰⵔⵏ ⴳ ⵜⵎⵏⴰⴹⵜ ⵏⵏⴽ, ⵙ ⵢⵉⵙⵎ ⵏ ⵓⵎⵓⵔ ⵏⵖ ⴷ ⵉⵎⴰⵏⴰⵔⵏ.' : 'Découvrez tous les citoyens qui se présentent dans votre région, qu\'ils soient sous l\'étiquette d\'un parti politique ou indépendants.'}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {regionsMapData.map((region, i) => {
              const regionSapCandidates = sapCandidates.filter(c => c.region === region.name && c.status === "valide").map(c => ({...c, type: 'sap'}));
              const regionPartyCandidates = publicCandidates.filter(c => c.region === region.name).map(c => ({
                ...c,
                type: 'party',
                firstName: c.firstName || c.prenomCandidat,
                lastName: c.lastName || c.nomCandidat,
                firstNameAr: c.firstNameAr || c.prenomCandidatAr,
                lastNameAr: c.lastNameAr || c.nomCandidatAr,
                listType: c.listType || c.typeListe,
                sociopro: c.sociopro || c.categorieSociopro
              }));
              const allRegionCandidates = [...regionSapCandidates, ...regionPartyCandidates];
              
              return (
                <Dialog key={region.id}>
                  <DialogTrigger asChild>
                    <button 
                      disabled={isLocked}
                      className={`group flex flex-col w-full p-5 rounded-2xl bg-card border border-border/80 shadow-sm items-center justify-center text-center overflow-hidden relative transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-80 ${!isLocked ? 'cursor-pointer hover:border-primary/50 hover:shadow-md' : ''}`}
                      onClick={() => {
                        setSelectedSapRegion(region.name);
                        setSelectedCandidate(null);
                      }}
                    >
                      <div className={`absolute top-0 left-0 w-full h-1 transition-all duration-300 opacity-50 ${!isLocked ? 'group-hover:opacity-100 group-hover:h-2' : ''}`} style={{ background: region.color }} />
                      
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 bg-muted transition-transform duration-300 shadow-sm ${!isLocked ? 'md:group-hover:scale-110' : ''}`} style={{ color: region.color }}>
                        <Users className="w-6 h-6" />
                      </div>
                      
                      <h4 className="font-display font-bold text-sm md:text-base leading-tight mb-1">{region.name}</h4>
                      <p className="text-xs text-muted-foreground font-medium" dir="rtl">{region.nameAr}</p>
                      
                      <Badge variant="outline" className={`mt-3 bg-background transition-colors ${!isLocked ? 'group-hover:bg-primary/5' : ''}`}>
                        {allRegionCandidates.length} candidat(s)
                      </Badge>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col rounded-3xl p-0">
                    <div className="h-2 w-full" style={{ background: region.color }} />
                    <DialogHeader className="px-6 py-4 border-b border-border/50 shrink-0">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-muted" style={{ color: region.color }}>
                          <Users className="w-5 h-5" />
                        </div>
                        <div>
                          <DialogTitle className="text-xl md:text-2xl font-display">
                            {lang === 'ar' ? 'المترشحون (أحزاب ومستقلون)' : lang === 'amz' ? 'ⵉⵎⵙⵜⴰⵢⵏ (ⵉⵎⵓⵔⴰⵏ ⴷ ⵉⵎⴰⵏⴰⵔⵏ)' : 'Candidats (Partis & Indépendants)'}
                          </DialogTitle>
                          <p className="text-sm text-muted-foreground">{region.name} ({region.nameAr})</p>
                        </div>
                      </div>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto p-6 bg-muted/10">
                      {selectedCandidate ? (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                          <Button variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground hover:text-foreground" onClick={() => setSelectedCandidate(null)}>
                            &larr; {lang === 'ar' ? 'العودة للقائمة' : 'Retour à la liste'}
                          </Button>
                          <div className="bg-card rounded-2xl border border-border/50 shadow-sm p-6 md:p-8">
                            <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
                              <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-2xl bg-muted flex items-center justify-center text-4xl md:text-5xl shadow-inner border border-border">
                                {selectedCandidate.photoUrl ? (
                                  <img src={selectedCandidate.photoUrl} alt="Candidat" className="w-full h-full object-cover rounded-2xl" />
                                ) : (
                                  selectedCandidate.symbol
                                )}
                              </div>
                              <div className="flex-1 space-y-4">
                                <div>
                                  <Badge className="mb-2 bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                                    {selectedCandidate.type === 'sap' ? 
                                      (lang === 'ar' ? 'بدون انتماء سياسي' : lang === 'amz' ? 'ⵡⴰⵔ ⴰⵙⵎⵉⵍ ⴰⵙⵔⵜⴰⵏ' : 'Sans Appartenance Politique')
                                      : (lang === 'ar' ? 'مرشح حزبي' : lang === 'amz' ? 'ⴰⵎⵙⵜⴰⵢ ⵏ ⵓⵎⵓⵔ' : 'Candidat Partisan')
                                    }
                                  </Badge>
                                  <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground">
                                    {selectedCandidate.firstName} {selectedCandidate.lastName}
                                  </h3>
                                  <h4 className="text-lg md:text-xl font-display text-muted-foreground mt-1" dir="rtl">
                                    {selectedCandidate.firstNameAr} {selectedCandidate.lastNameAr}
                                  </h4>
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  <div className="bg-muted/50 p-3 rounded-xl">
                                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">{lang === 'ar' ? 'العمالة / المستوى' : lang === 'amz' ? 'ⵜⴰⵙⴳⴰ / ⴰⵙⵡⵉⵔ' : 'Province / Niveau'}</p>
                                    <p className="font-semibold text-sm">{selectedCandidate.listType === 'regionale' || selectedCandidate.province === selectedCandidate.region || selectedCandidate.province === 'Régional' ? (lang === 'ar' ? 'جهوي' : lang === 'amz' ? 'ⴰⵏⵎⵏⴰⴹ' : 'Régional') : selectedCandidate.province}</p>
                                  </div>
                                  <div className="bg-muted/50 p-3 rounded-xl">
                                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">{lang === 'ar' ? 'المهنة' : lang === 'amz' ? 'ⵜⴰⵣⵣⵓⵍⵜ' : 'Profession'}</p>
                                    <p className="font-semibold text-sm">{selectedCandidate.sociopro}</p>
                                  </div>
                                  <div className="bg-muted/50 p-3 rounded-xl">
                                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">{lang === 'ar' ? 'العمر' : lang === 'amz' ? 'ⴰⵡⵜⴰⵢ' : 'Âge'}</p>
                                    <p className="font-semibold text-sm" dir={lang === 'ar' ? 'rtl' : 'ltr'}>{selectedCandidate.age} {lang === 'ar' ? 'سنة' : lang === 'amz' ? 'ⵉⵙⴳⴳⵯⴰⵙⵏ' : 'ans'}</p>
                                  </div>
                                  <div className="bg-muted/50 p-3 rounded-xl">
                                    <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">{lang === 'ar' ? 'الجنس' : lang === 'amz' ? 'ⴰⵏⴰⵡ' : 'Genre'}</p>
                                    <p className="font-semibold text-sm">{selectedCandidate.genre === "F" ? (lang === 'ar' ? 'امرأة' : lang === 'amz' ? 'ⵜⴰⵎⵖⴰⵔⵜ' : 'Femme') : (lang === 'ar' ? 'رجل' : lang === 'amz' ? 'ⴰⵔⴳⴰⵣ' : 'Homme')}</p>
                                  </div>
                                </div>
                                
                                <div className="mt-6 space-y-3 pt-6 border-t border-border/50">
                                  <h5 className="font-bold text-foreground uppercase text-xs tracking-widest text-muted-foreground">
                                    {lang === 'ar' ? 'المحور الرئيسي / الأولوية' : lang === 'amz' ? 'ⴰⵎⵎⴰⵙ ⴰⵎⵇⵇⵔⴰⵏ / ⵜⴰⵣⵡⵓⵔⵉ' : 'Axe Majeur / Priorité'}
                                  </h5>
                                  <p className="text-base text-foreground bg-primary/5 p-4 rounded-xl border-l-4 border-primary">
                                    {selectedCandidate.focusFr}
                                  </p>
                                  <p className="text-base text-right bg-primary/5 p-4 rounded-xl border-r-4 border-primary font-display" dir="rtl">
                                    {selectedCandidate.focusAr}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="animate-in fade-in duration-300">
                          {allRegionCandidates.length > 0 ? (
                            <div className="bg-card rounded-2xl border border-border/50 overflow-hidden shadow-sm">
                              <table className="w-full text-sm text-left">
                                <thead className="bg-muted/50 font-semibold text-muted-foreground uppercase text-xs tracking-wider">
                                  <tr>
                                    <th className="px-4 py-3 md:py-4">{lang === 'ar' ? 'المترشح' : lang === 'amz' ? 'ⴰⵎⵙⵜⴰⵢ' : 'Candidat'}</th>
                                    <th className="px-4 py-3 md:py-4">{lang === 'ar' ? 'العمالة / المستوى' : lang === 'amz' ? 'ⵜⴰⵙⴳⴰ / ⴰⵙⵡⵉⵔ' : 'Province / Niveau'}</th>
                                    <th className="px-4 py-3 md:py-4 hidden sm:table-cell">{lang === 'ar' ? 'الملف الشخصي' : lang === 'amz' ? 'ⴰⴼⴰⵢⵍⵓ' : 'Profil'}</th>
                                    <th className={`px-4 py-3 md:py-4 ${lang === 'ar' ? 'text-left' : 'text-right'}`}>{lang === 'ar' ? 'إجراء' : lang === 'amz' ? 'ⴰⵎⵓⵙⵙⵓ' : 'Action'}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {allRegionCandidates.map(c => (
                                    <tr key={c.id} className="border-t border-border/50 hover:bg-muted/30 transition-colors cursor-pointer group" onClick={() => setSelectedCandidate(c)}>
                                      <td className="px-4 py-3 md:py-4">
                                        <div className="flex items-center gap-3">
                                          <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center text-xl shadow-sm border border-border group-hover:border-primary/30 transition-colors">
                                            {c.photoUrl ? <img src={c.photoUrl} className="w-full h-full rounded-xl object-cover" /> : c.symbol}
                                          </div>
                                          <div>
                                            <p className="font-bold text-foreground">{c.firstName} {c.lastName}</p>
                                            <p className="text-xs text-muted-foreground font-medium mt-0.5">{c.firstNameAr} {c.lastNameAr}</p>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 md:py-4 font-medium">{(c as any).listType === 'regionale' || c.province === region.name || c.province === 'Régional' ? (lang === 'ar' ? 'جهوي' : lang === 'amz' ? 'ⴰⵏⵎⵏⴰⴹ' : 'Régional') : c.province}</td>
                                      <td className="px-4 py-3 md:py-4 hidden sm:table-cell text-xs text-muted-foreground" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                                        {c.sociopro} • {c.age} {lang === 'ar' ? 'سنة' : lang === 'amz' ? 'ⵉⵙⴳⴳⵯⴰⵙⵏ' : 'ans'}
                                      </td>
                                      <td className={`px-4 py-3 md:py-4 ${lang === 'ar' ? 'text-left' : 'text-right'}`}>
                                        <Button size="sm" variant="outline" className="group-hover:bg-primary group-hover:text-white transition-colors">
                                          {lang === 'ar' ? 'عرض الملف' : lang === 'amz' ? 'ⵥⵕ ⴰⴼⴰⵢⵍⵓ' : 'Voir Profil'}
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-12 px-4 bg-card rounded-2xl border border-dashed border-border/80">
                              <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                              <h4 className="text-lg font-bold text-foreground mb-1">{lang === 'ar' ? 'قيد التجميع...' : lang === 'amz' ? 'ⴰⵔ ⵉⵜⵜⵓⵙⵎⵓⵏ...' : 'En cours de consolidation...'}</h4>
                              <p className="text-muted-foreground">{lang === 'ar' ? 'لم يتم إدخال أي مرشحين لهذه المعايير بعد.' : lang === 'amz' ? 'ⵓⵔ ⵉⵍⵍⵉ ⴰⵡⴷ ⵢⴰⵏ ⵓⵎⵙⵜⴰⵢ.' : 'Aucun candidat n\'a encore été saisi pour cette région.'}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartiesSection;
